import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { construirTablero, estadoCelda } from '../../domain/planningBoard';
import type { Celda, Metrica, PosicionPlan } from '../../domain/planningBoard';
import {
  ESTILO_CELDA, METRICAS, ORDEN_LEYENDA, diasVisibles, esFinDeSemana, etiquetaCelda,
  etiquetaMes, fechaLarga, inicialDiaSemana, numeroDeDia, resumenFila,
} from '../../domain/planningBoardView';
import {
  fetchDetalleDia, fetchPosicionesMes, listCampaniasDelMes, type DetalleDia,
} from '../../api/planning';
import { useAuth } from '../../components/AuthProvider';
import { soloSupervisaTiendas } from '../../domain/permissions';
import FilaTarjeta from '../../components/FilaTarjeta';

const TZ = 'America/Lima';

/**
 * Matriz de planificación del mes: campañas (filas) × días (columnas).
 *
 * NO es un calendario semanal: la pregunta que responde es "¿cómo va el mes de TODAS mis
 * campañas?", y para eso hay que ver las campañas una debajo de la otra sobre el mismo eje
 * de días. Cada celda dice "logrado / programado" —el número va SIEMPRE, porque 0/20 y 0/2
 * son problemas de tamaño distinto y el color no los distingue—.
 *
 * El interruptor cambia qué significa "logrado" en toda la pantalla (ver `METRICAS`).
 *
 * No existe el estado azul "marcaron sin estar programado" del tablero anterior: aquí no es
 * posible: para marcar hace falta una asignación APROBADA sobre una posición programada.
 */

const ESTADO_DETALLE: Record<DetalleDia['estado'], { label: string; cls: string }> = {
  sin_asignar: { label: 'Sin asignar', cls: 'bg-gray-200 text-gray-800' },
  asignado: { label: 'Asignado sin marcar', cls: 'bg-blue-100 text-blue-800' },
  ingreso: { label: 'Ingresó', cls: 'bg-amber-100 text-amber-800' },
  completo: { label: 'Completo', cls: 'bg-green-100 text-green-800' },
  falta: { label: 'Falta', cls: 'bg-red-100 text-red-800' },
};

// Se importa de domain/flags: cada copia local se quedaba sin las banderas nuevas y la
// pantalla mostraba el codigo crudo ('salida_temprana') en vez del texto en espanol.
import { FLAG_LABEL } from '../../domain/flags';

/**
 * Celda abierta en el detalle: SOLO la campaña y el día sobre los que se hizo clic.
 *
 * A propósito no guarda una copia de la celda: sus números dependen de la métrica, y una copia
 * seguiría mostrando los de "Asignación" bajo el rótulo "Asistencia" al mover el interruptor.
 * La celda se vuelve a leer del tablero en cada pintado.
 */
interface CeldaAbierta {
  campaignId: string;
  date: string;
}

const mesActual = (): string => formatInTimeZone(new Date(), TZ, 'yyyy-MM');

/** El `<input type="month">` entrega `YYYY-MM`; el tablero trabaja con año y mes numéricos. */
function partesMes(valor: string): { anio: number; mes: number } {
  return { anio: Number(valor.slice(0, 4)), mes: Number(valor.slice(5, 7)) };
}

export default function Planificacion() {
  const { roles } = useAuth();
  const [mesTexto, setMesTexto] = useState(mesActual);
  const [metrica, setMetrica] = useState<Metrica>('asignacion');
  const [campaignId, setCampaignId] = useState('');
  const [ocultarVacios, setOcultarVacios] = useState(false);

  const [campanias, setCampanias] = useState<{ id: string; etiqueta: string }[]>([]);
  const [posiciones, setPosiciones] = useState<PosicionPlan[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const [abierta, setAbierta] = useState<CeldaAbierta | null>(null);
  const [detalle, setDetalle] = useState<DetalleDia[]>([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState('');

  const { anio, mes } = partesMes(mesTexto);
  const mesValido = Number.isInteger(anio) && anio >= 2000 && mes >= 1 && mes <= 12;

  // Las campañas del filtro dependen solo del mes.
  useEffect(() => {
    if (!mesValido) return;
    let vivo = true;
    listCampaniasDelMes(anio, mes)
      .then((lista) => {
        if (!vivo) return;
        setCampanias(lista);
        // Si la campaña filtrada no trabaja este mes, el filtro se suelta: dejarlo puesto
        // mostraría un mes vacío y haría creer que no hay programación cargada.
        setCampaignId((actual) => (actual && !lista.some((c) => c.id === actual) ? '' : actual));
      })
      .catch(() => { if (vivo) setCampanias([]); });
    return () => { vivo = false; };
  }, [anio, mes, mesValido]);

  // Las posiciones traen lo programado con su asignación y su marcado: el interruptor de
  // métrica NO vuelve a consultar la base, solo recuenta lo que ya está en memoria.
  useEffect(() => {
    if (!mesValido) return;
    let vivo = true;
    setCargando(true);
    setError('');
    // El detalle abierto pertenecía al mes (o a la campaña) anterior: se cierra y su lectura
    // en vuelo queda descartada por el turno.
    turnoDetalle.current += 1;
    setAbierta(null);
    setDetalle([]);
    fetchPosicionesMes(anio, mes, campaignId || undefined)
      .then((filas) => { if (vivo) setPosiciones(filas); })
      .catch((e: unknown) => {
        if (!vivo) return;
        setPosiciones([]);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [anio, mes, campaignId, mesValido]);

  const tablero = useMemo(
    () => construirTablero(posiciones, mesValido ? anio : 2000, mesValido ? mes : 1, metrica),
    [posiciones, anio, mes, metrica, mesValido],
  );

  const etiquetaDe = useMemo(() => {
    const mapa = new Map(campanias.map((c) => [c.id, c.etiqueta]));
    // Una campaña con posiciones en el mes pero fuera del rango start_date/end_date no está
    // en el filtro: se muestra igual, identificada, porque su programación existe.
    return (id: string): string => mapa.get(id) ?? `Campaña ${id.slice(0, 8)}`;
  }, [campanias]);

  // El dominio ordena por id (estable pero arbitrario para quien mira): en pantalla se
  // ordena por el nombre con el que el jefe de operaciones llama a la campaña.
  const filas = useMemo(
    () => [...tablero.filas].sort((a, b) =>
      etiquetaDe(a.campaignId).localeCompare(etiquetaDe(b.campaignId), 'es')),
    [tablero.filas, etiquetaDe],
  );

  const dias = useMemo(() => diasVisibles(tablero, ocultarVacios), [tablero, ocultarVacios]);
  const diasOcultos = tablero.dias.length - dias.length;
  const info = METRICAS[metrica];

  // La celda del detalle se relee del tablero: así el interruptor de métrica también recuenta
  // su encabezado. Si la celda ya no existe (cambió el filtro), el detalle no se pinta.
  const celdaAbierta: Celda | null = abierta
    ? tablero.filas.find((f) => f.campaignId === abierta.campaignId)?.celdas[abierta.date] ?? null
    : null;

  /**
   * Turno de la petición del detalle. Dos clics seguidos disparan dos lecturas y la primera
   * puede contestar después: sin este contador, las filas de la celda vieja quedarían bajo el
   * encabezado de la nueva, que es peor que no mostrar nada.
   */
  const turnoDetalle = useRef(0);

  async function abrirCelda(campaignIdCelda: string, date: string) {
    const turno = turnoDetalle.current + 1;
    turnoDetalle.current = turno;
    setAbierta({ campaignId: campaignIdCelda, date });
    setDetalle([]);
    setErrorDetalle('');
    setCargandoDetalle(true);
    try {
      const filasDia = await fetchDetalleDia(campaignIdCelda, date);
      if (turnoDetalle.current !== turno) return;
      setDetalle(filasDia);
    } catch (e) {
      if (turnoDetalle.current !== turno) return;
      setErrorDetalle(e instanceof Error ? e.message : String(e));
    } finally {
      if (turnoDetalle.current === turno) setCargandoDetalle(false);
    }
  }

  const puedeImportar = roles.includes('admin') || roles.includes('comercial');
  // El supervisor ve solo sus tiendas: un mes vacío para él no dice que no haya programación.
  const soloTiendas = soloSupervisaTiendas(roles);
  const anchoCelda = 'w-12 min-w-[3rem]';

  return (
    // `min-w-0` para que la matriz ancha no empuje el ancho de la página: el desplazamiento
    // horizontal es SOLO del recuadro de la matriz (la página nunca se corre de lado).
    <div className="min-w-0 p-4 lg:p-6">
      <h1 className="text-xl font-semibold">
        Planificación del mes — programado vs logrado por día
      </h1>
      <p className="mt-1 text-sm text-gray-600">
        Cada celda es "logrado / programado" de una campaña en un día. Haz clic en una celda
        para ver ese día tienda por tienda.
      </p>

      {/* Interruptor de métrica: cambia el significado de TODAS las celdas. */}
      <div className="mt-4 rounded border bg-white p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded border border-blue-600 p-0.5" role="group"
            aria-label="Métrica del tablero">
            {(['asignacion', 'asistencia'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMetrica(m)}
                aria-pressed={metrica === m}
                className={`min-h-[44px] rounded px-4 text-sm ${
                  metrica === m
                    ? 'bg-blue-600 font-semibold text-white'
                    : 'text-blue-700 hover:bg-blue-50'
                }`}
              >
                {METRICAS[m].titulo}
              </button>
            ))}
          </div>
          <p className="text-sm">
            <span className="font-semibold">Estás viendo {info.titulo}</span>
            {': '}
            {info.logrado}.
          </p>
        </div>
        <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
          {(['asignacion', 'asistencia'] as const).map((m) => (
            <li key={m} className={metrica === m ? 'font-semibold text-gray-900' : ''}>
              <span className="font-semibold">{METRICAS[m].titulo}</span>
              {`: ${METRICAS[m].pregunta} — ${METRICAS[m].cuando}.`}
            </li>
          ))}
        </ul>
      </div>

      {/* Filtros */}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">
          Mes
          <input type="month" className="rounded border px-2 py-1" value={mesTexto}
            onChange={(e) => setMesTexto(e.target.value)} />
        </label>
        <label className="flex min-w-0 flex-col text-sm">
          Campaña
          <select className="max-w-[16rem] rounded border px-2 py-1" value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">Todas las campañas</option>
            {campanias.map((c) => (
              <option key={c.id} value={c.id}>{c.etiqueta}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={ocultarVacios}
            onChange={(e) => setOcultarVacios(e.target.checked)} />
          Ocultar días sin programación
        </label>
        {ocultarVacios && diasOcultos > 0 && (
          <span className="text-xs text-gray-500">
            {diasOcultos} día(s) del mes sin programación, ocultos
          </span>
        )}
      </div>

      {/* Leyenda: siempre visible y en palabras, no solo en colores. */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
        {ORDEN_LEYENDA.map((estado) => (
          <span key={estado} className="inline-flex items-center gap-1">
            <span className={`inline-block h-3 w-3 rounded ${ESTILO_CELDA[estado].claseMuestra}`}
              aria-hidden="true" />
            <span className="font-medium">{ESTILO_CELDA[estado].palabra}</span>
            <span className="text-gray-500">= {ESTILO_CELDA[estado].explicacion}</span>
          </span>
        ))}
      </div>

      {!mesValido && (
        <p className="mt-4 text-sm text-red-600">Elige un mes válido.</p>
      )}
      {error && (
        <p className="mt-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </p>
      )}
      {cargando && <p className="mt-4 text-sm text-gray-500">Cargando el mes…</p>}

      {/* Vacío honesto: se dice con palabras y se ofrece la salida. */}
      {!cargando && !error && mesValido && filas.length === 0 && (
        <div className="mt-4 rounded border bg-white p-4 text-sm">
          <p className="font-medium">
            {soloTiendas
              ? `Ninguna de tus tiendas tiene programación en ${etiquetaMes(anio, mes)}${campaignId ? ` para ${etiquetaDe(campaignId)}` : ''}.`
              : campaignId
                ? `No hay programación cargada para ${etiquetaDe(campaignId)} en ${etiquetaMes(anio, mes)}.`
                : `No hay programación cargada para ${etiquetaMes(anio, mes)}.`}
          </p>
          {campaignId ? (
            <button type="button" onClick={() => setCampaignId('')}
              className="mt-1 text-blue-600 underline">
              Ver todas las campañas del mes
            </button>
          ) : soloTiendas ? null : puedeImportar ? (
            <p className="mt-1 text-gray-600">
              <Link to="/panel/importar-programacion" className="text-blue-600 underline">
                Importar programación
              </Link>
              {' '}del Excel del cliente para que el mes aparezca aquí.
            </p>
          ) : (
            <p className="mt-1 text-gray-600">
              La carga la hace el comercial o el jefe de operaciones: pídeles la programación
              del mes.
            </p>
          )}
        </div>
      )}

      {!cargando && !error && mesValido && filas.length > 0 && (
        <>
          <p className="mt-4 text-xs text-gray-500 lg:hidden">
            Desliza la matriz de lado para ver el resto del mes; la columna de campaña queda fija.
          </p>
          {/* El ÚNICO elemento con desplazamiento horizontal de la pantalla. */}
          <div className="mt-2 overflow-x-auto border bg-white">
            <table className="border-collapse text-xs">
              <caption className="sr-only">
                {`Programado y ${info.titulo.toLowerCase()} por campaña y día de ${etiquetaMes(anio, mes)}`}
              </caption>
              <thead>
                <tr className="bg-gray-100">
                  {/* Columna fija: sin ella se pierde de vista de qué campaña es la fila. */}
                  <th scope="col"
                    className="sticky left-0 z-20 min-w-[10rem] max-w-[14rem] border-b border-r bg-gray-100 p-2 text-left">
                    Campaña
                  </th>
                  {dias.map((d) => (
                    <th key={d} scope="col"
                      className={`border-b border-r p-1 text-center font-medium ${anchoCelda} ${
                        esFinDeSemana(d) ? 'text-gray-500' : ''
                      }`}>
                      <span className="block leading-tight">{numeroDeDia(d)}</span>
                      <span className="block text-[10px] font-normal leading-tight text-gray-500">
                        {inicialDiaSemana(d)}
                      </span>
                    </th>
                  ))}
                  <th scope="col" className="border-b bg-gray-200 p-2 text-center">
                    <span className="block">TOTAL</span>
                    <span className="block text-[10px] font-normal text-gray-600">
                      logrado/prog · %
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => {
                  const estadoTotal = estadoCelda(fila.totalProgramado, fila.totalLogrado);
                  return (
                    <tr key={fila.campaignId} className="align-top">
                      <th scope="row"
                        className="sticky left-0 z-10 min-w-[10rem] max-w-[14rem] border-b border-r bg-white p-2 text-left font-normal">
                        <span className="block break-words font-semibold">
                          {etiquetaDe(fila.campaignId)}
                        </span>
                        <span className="block break-words text-[11px] text-gray-500">
                          {resumenFila(fila)}
                        </span>
                      </th>
                      {dias.map((d) => {
                        const celda = fila.celdas[d];
                        const estilo = ESTILO_CELDA[celda.estado];
                        if (celda.programado <= 0) {
                          return (
                            <td key={d}
                              className={`border-b border-r ${anchoCelda} h-11 ${estilo.clase}`}
                              title={`${etiquetaDe(fila.campaignId)} · ${fechaLarga(d)}: sin programación`}
                            />
                          );
                        }
                        return (
                          <td key={d} className={`border-b border-r p-0 ${anchoCelda}`}>
                            <button
                              type="button"
                              onClick={() => void abrirCelda(fila.campaignId, d)}
                              aria-label={`${etiquetaDe(fila.campaignId)}, ${fechaLarga(d)}: ${
                                celda.logrado} de ${celda.programado} — ${estilo.palabra}`}
                              className={`h-11 w-full text-center tabular-nums hover:ring-2 hover:ring-inset hover:ring-blue-600 ${
                                estilo.clase
                              } ${
                                abierta && abierta.campaignId === fila.campaignId && abierta.date === d
                                  ? 'ring-2 ring-inset ring-blue-700'
                                  : ''
                              }`}
                            >
                              {etiquetaCelda(celda)}
                            </button>
                          </td>
                        );
                      })}
                      <td className={`border-b p-1 text-center tabular-nums ${ESTILO_CELDA[estadoTotal].clase}`}>
                        <span className="block whitespace-nowrap">
                          {fila.totalLogrado}/{fila.totalProgramado}
                        </span>
                        <span className="block text-[10px]">{fila.porcentaje}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Fila TOTAL por día: muestra de un golpe qué día de la operación se cayó. */}
              <tfoot>
                <tr>
                  <th scope="row"
                    className="sticky left-0 z-10 border-r border-t bg-gray-100 p-2 text-left">
                    <span className="block">TOTAL del día</span>
                    <span className="block text-[11px] font-normal text-gray-500">
                      todas las campañas mostradas
                    </span>
                  </th>
                  {dias.map((d) => {
                    const total = tablero.totalesPorDia[d];
                    const estilo = ESTILO_CELDA[total.estado];
                    return (
                      <td key={d}
                        className={`border-r border-t p-1 text-center tabular-nums ${anchoCelda} ${estilo.clase}`}
                        title={`${fechaLarga(d)}: ${etiquetaCelda(total) || 'sin programación'}`}>
                        {total.programado > 0 && (
                          <>
                            <span className="block leading-tight">{total.logrado}</span>
                            <span className="block leading-tight">/{total.programado}</span>
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td className={`border-t p-1 text-center font-semibold tabular-nums ${
                    ESTILO_CELDA[estadoCelda(
                      tablero.granTotal.programado, tablero.granTotal.logrado,
                    )].clase
                  }`}>
                    <span className="block whitespace-nowrap">
                      {tablero.granTotal.logrado}/{tablero.granTotal.programado}
                    </span>
                    <span className="block text-[10px]">{tablero.granTotal.porcentaje}%</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {`${filas.length} campaña(s) · ${info.logrado} · totales del mes: `}
            {`${tablero.granTotal.logrado} de ${tablero.granTotal.programado} días-tienda `}
            {`(${tablero.granTotal.porcentaje}%)`}
          </p>
        </>
      )}

      {abierta && celdaAbierta && (
        <section className="mt-6 rounded border bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="break-words text-base font-semibold">
                {etiquetaDe(abierta.campaignId)} · {fechaLarga(abierta.date)}
              </h2>
              <p className="text-xs text-gray-600">
                {`${info.titulo}: ${celdaAbierta.logrado} de ${celdaAbierta.programado} ` +
                  `puesto(s) · ${info.logrado}`}
              </p>
            </div>
            <button type="button"
              onClick={() => { turnoDetalle.current += 1; setAbierta(null); }}
              className="min-h-[44px] rounded border px-3 text-sm">
              Cerrar detalle
            </button>
          </div>

          {cargandoDetalle && <p className="mt-3 text-sm text-gray-500">Cargando el día…</p>}
          {errorDetalle && (
            <p className="mt-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {errorDetalle}
            </p>
          )}

          {!cargandoDetalle && !errorDetalle && (
            <>
              {/* En celular: una tarjeta por tienda (sin desplazamiento horizontal). */}
              <div className="mt-3 space-y-2 lg:hidden">
                {/* La clave lleva la POSICIÓN en la lista: una tienda puede tener dos turnos
                    iguales sin asignar (slot 0 y 1) y con una clave repetida se pisarían,
                    escondiendo uno de los dos huecos. El orden de la lista es estable. */}
                {detalle.map((d, i) => (
                  <FilaTarjeta
                    key={`${i}-${d.pdvCode}-${d.startTime}`}
                    titulo={d.pdvName}
                    subtitulo={d.pdvCode}
                    estados={
                      <>
                        <span className={`rounded px-1.5 py-0.5 text-xs ${ESTADO_DETALLE[d.estado].cls}`}>
                          {ESTADO_DETALLE[d.estado].label}
                        </span>
                        {d.flags.map((f) => (
                          <span key={f} className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                            {FLAG_LABEL[f] ?? f}
                          </span>
                        ))}
                      </>
                    }
                    campos={[
                      { etiqueta: 'Recurso', valor: d.workerName ?? '— sin asignar —' },
                      { etiqueta: 'DNI', valor: d.workerDni },
                      { etiqueta: 'Horario', valor: `${d.startTime}–${d.endTime}` },
                      { etiqueta: 'Ingreso', valor: d.horaIngreso },
                      { etiqueta: 'Salida', valor: d.horaSalida },
                    ]}
                  />
                ))}
                {detalle.length === 0 && (
                  <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">
                    Ese día ya no tiene posiciones en esta campaña.
                  </p>
                )}
              </div>

              <div className="mt-3 hidden overflow-x-auto lg:block">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">Tienda</th>
                      <th className="p-2">Horario</th>
                      <th className="p-2 text-left">Recurso</th>
                      <th className="p-2">DNI</th>
                      <th className="p-2">Estado</th>
                      <th className="p-2">Ingreso</th>
                      <th className="p-2">Salida</th>
                      <th className="p-2 text-left">Alertas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.map((d, i) => (
                      <tr key={`${i}-${d.pdvCode}-${d.startTime}`} className="border-t">
                        <td className="p-2">
                          {d.pdvName}
                          {d.pdvCode && <span className="text-xs text-gray-500"> · {d.pdvCode}</span>}
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {d.startTime}–{d.endTime}
                        </td>
                        <td className="p-2">
                          {d.workerName ?? <span className="text-gray-400">— sin asignar —</span>}
                        </td>
                        <td className="p-2 text-center tabular-nums">{d.workerDni ?? '—'}</td>
                        <td className="p-2 text-center">
                          <span className={`rounded px-1.5 py-0.5 text-xs ${ESTADO_DETALLE[d.estado].cls}`}>
                            {ESTADO_DETALLE[d.estado].label}
                          </span>
                        </td>
                        <td className="p-2 text-center tabular-nums">{d.horaIngreso ?? '—'}</td>
                        <td className="p-2 text-center tabular-nums">{d.horaSalida ?? '—'}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {d.flags.map((f) => (
                              <span key={f} className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                                {FLAG_LABEL[f] ?? f}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {detalle.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-gray-500">
                          Ese día ya no tiene posiciones en esta campaña.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}
